
// Import Dependencies
import { CalendarIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import flatpickr from "flatpickr";
import flatpickrCSS from "flatpickr/dist/themes/light.css?inline";

// Local Imports
import { Input } from "components/ui";
import { useLocaleContext } from "app/contexts/locale/context";
import { useMergedRef } from "hooks";
import { locales } from "i18n/langs";
import { Flatpickr } from "./Flatpickr";
import {
  injectStyles,
  insertStylesToHead,
  makeStyleTag,
} from "utils/dom/injectStylesToHead";

// ----------------------------------------------------------------------

const styles = `@layer vendor {
  ${flatpickrCSS}
}`;

const sheet = makeStyleTag();

injectStyles(sheet, styles);
insertStylesToHead(sheet);

const parseSingleDateStr = (str) => {
  if (!str || typeof str !== "string") return null;
  const trimmed = str.trim();

  const parts = trimmed.split(/[\/\-\.]/);
  if (parts.length === 3) {
    let day, month, year;
    if (parts[0].length === 4) {
      year = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10);
      day = parseInt(parts[2], 10);
    } else {
      day = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10);
      year = parseInt(parts[2], 10);
    }

    if (
      !isNaN(day) &&
      !isNaN(month) &&
      !isNaN(year) &&
      year >= 1900 &&
      year <= 2100 &&
      month >= 1 &&
      month <= 12 &&
      day >= 1 &&
      day <= 31
    ) {
      const parsedDate = new Date(year, month - 1, day);
      if (
        parsedDate.getFullYear() === year &&
        parsedDate.getMonth() === month - 1 &&
        parsedDate.getDate() === day
      ) {
        return parsedDate;
      }
    }
  }
  return null;
};

const flexibleParseDate = (datestr) => {
  if (!datestr || typeof datestr !== "string") return null;
  const trimmed = datestr.trim();
  if (!trimmed) return null;

  if (trimmed.includes(" to ") || trimmed.includes(" - ")) {
    const rangeParts = trimmed.split(/ to | \- /);
    if (rangeParts.length === 2) {
      const d1 = parseSingleDateStr(rangeParts[0]);
      const d2 = parseSingleDateStr(rangeParts[1]);
      if (d1 && d2) return [d1, d2];
    }
  }

  return parseSingleDateStr(trimmed);
};

const formatTypedDate = (val, isRange) => {
  if (!val) return "";
  if (isRange) {
    return val.replace(/[^0-9\/t o\-]/gi, "").slice(0, 24);
  }

  const digits = val.replace(/\D/g, "").slice(0, 8);
  let day = digits.slice(0, 2);
  let month = digits.slice(2, 4);
  let year = digits.slice(4, 8);

  if (day.length === 2) {
    let dayNum = parseInt(day, 10);
    if (dayNum > 31) day = "31";
    if (dayNum === 0) day = "01";
  }

  if (month.length === 2) {
    let monthNum = parseInt(month, 10);
    if (monthNum > 12) month = "12";
    if (monthNum === 0) month = "01";
  }

  if (digits.length <= 2) {
    if (val.endsWith("/") && digits.length === 2) return `${day}/`;
    return day;
  }

  if (digits.length <= 4) {
    if (val.endsWith("/") && digits.length === 4) return `${day}/${month}/`;
    return `${day}/${month}`;
  }

  return `${day}/${month}/${year}`;
};

const DatePicker = forwardRef(
  (
    {
      options: userOptions,
      className = "text-sm",
      isCalendar = false,
      hasCalenderIcon = true,
      placeholder,
      value,
      ...props
    },
    ref,
  ) => {
    const fp = useRef(null);
    const { locale } = useLocaleContext();
    const [localeData, setLocaleData] = useState(null);

    useEffect(() => {
      const loadLocale = async () => {
        const load = locales[locale].flatpickr;
        if (load) {
          const loadedLocale = await load();
          setLocaleData(loadedLocale);
        } else {
          setLocaleData(null); // Fallback if no locale is found
        }
      };

      loadLocale();
    }, [locale]);

    const isRangeMode =
      userOptions?.mode === "range" ||
      (typeof placeholder === "string" &&
        placeholder.toLowerCase().includes("range"));

    const handleInvalidCleanup = (selectedDates, instance) => {
      const targetInstance = instance || fp.current?.flatpickr;
      if (targetInstance && targetInstance.input) {
        const val = targetInstance.input.value;
        if (val) {
          const parsed = flexibleParseDate(val);
          if (!parsed) {
            if (targetInstance.selectedDates && targetInstance.selectedDates.length > 0) {
              targetInstance.setDate(targetInstance.selectedDates, false);
            } else {
              targetInstance.clear();
            }
          }
        } else {
          if (targetInstance.selectedDates && targetInstance.selectedDates.length > 0) {
            targetInstance.clear();
          }
        }
      }
    };

    const options = {
      inline: isCalendar,
      locale: localeData,
      disableMobile: true,
      dateFormat: "d/m/Y",
      altFormat: "d/m/Y",
      allowInput: true,
      parseDate: flexibleParseDate,
      onReady: (selectedDates, dateStr, instance) => {
        if (instance?.input) {
          instance.input.setAttribute("autocomplete", "off");
          instance.input.setAttribute("autocorrect", "off");
          instance.input.setAttribute("autocapitalize", "off");
          instance.input.setAttribute("spellcheck", "false");
          instance.input.setAttribute("data-lpignore", "true");
          instance.input.setAttribute("data-form-type", "other");
        }
        if (instance?.altInput) {
          instance.altInput.setAttribute("autocomplete", "off");
          instance.altInput.setAttribute("autocorrect", "off");
          instance.altInput.setAttribute("autocapitalize", "off");
          instance.altInput.setAttribute("spellcheck", "false");
          instance.altInput.setAttribute("data-lpignore", "true");
          instance.altInput.setAttribute("data-form-type", "other");
        }
        if (userOptions?.onReady) {
          userOptions.onReady(selectedDates, dateStr, instance);
        }
      },
      onOpen: (selectedDates, dateStr, instance) => {
        if (instance?.input) {
          instance.input.setAttribute("autocomplete", "off");
          instance.input.setAttribute("data-lpignore", "true");
        }
        if (instance?.altInput) {
          instance.altInput.setAttribute("autocomplete", "off");
          instance.altInput.setAttribute("data-lpignore", "true");
        }
        if (userOptions?.onOpen) {
          userOptions.onOpen(selectedDates, dateStr, instance);
        }
      },
      onClose: (selectedDates, dateStr, instance) => {
        handleInvalidCleanup(selectedDates, instance);
        if (userOptions?.onClose) {
          userOptions.onClose(selectedDates, dateStr, instance);
        }
      },
      ...(isRangeMode ? { mode: "range" } : {}),
      ...userOptions,
    };

    useEffect(() => {
      const calendarContainer = fp.current?.flatpickr?.calendarContainer;

      if (calendarContainer) {
        calendarContainer.classList.toggle("is-calendar", isCalendar);
      }
    }, [isCalendar]);

    useImperativeHandle(ref, () => {
      return {
        focus() {
          fp.current.flatpickr.input.focus();
        },
        blur() {
          fp.current.flatpickr.input.blur();
        },
        clear() {
          fp.current?.flatpickr?.clear();
        },
      };
    }, []);

    const mergedRef = useMergedRef(fp, ref);

    const isReadOnly = props.readOnly ?? !options?.allowInput;

    const defaultPlaceholder =
      options?.mode === "range" ? "DD/MM/YYYY to DD/MM/YYYY" : "DD/MM/YYYY";
    let resolvedPlaceholder = placeholder ?? defaultPlaceholder;

    if (
      typeof resolvedPlaceholder === "string" &&
      resolvedPlaceholder.toLowerCase().startsWith("choose ")
    ) {
      if (resolvedPlaceholder.toLowerCase() === "choose date") {
        resolvedPlaceholder = "DD/MM/YYYY";
      } else if (resolvedPlaceholder.toLowerCase() === "choose date range") {
        resolvedPlaceholder = "DD/MM/YYYY to DD/MM/YYYY";
      } else {
        resolvedPlaceholder = resolvedPlaceholder.replace(
          /^choose\s+/i,
          "Select or enter "
        );
      }
    }

    const handleCalendarIconClick = (e) => {
      e.stopPropagation();
      fp.current?.flatpickr?.open();
    };

    return (
      <Flatpickr
        className={clsx(
          !options?.allowInput && "cursor-pointer",
          isCalendar && "hidden",
          className,
        )}
        options={options}
        ref={mergedRef}
        value={value}
        {...props}
        render={({ value: _v, defaultValue: _dv, ...flatpickrProps }, ref) => {
          return isCalendar ? (
            <input
              ref={ref}
              readOnly={isReadOnly}
              placeholder={resolvedPlaceholder}
              {...flatpickrProps}
            />
          ) : (
            <Input
              ref={ref}
              labelProps={{
                onClick: (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                },
                ...props.labelProps
              }}
              prefix={
                !userOptions?.inline &&
                hasCalenderIcon && (
                  <button
                    type="button"
                    onClick={handleCalendarIconClick}
                    className="focus:outline-none cursor-pointer"
                  >
                    <CalendarIcon className="size-5 text-gray-400" />
                  </button>
                )
              }
              readOnly={isReadOnly}
              placeholder={resolvedPlaceholder}
              type={options?.altInput ? "hidden" : "text"}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-lpignore="true"
              data-form-type="other"
              className={clsx("h-[38px] py-1.5 text-sm", className)}
              onKeyDown={(e) => {
                if (!isReadOnly && options?.allowInput) {
                  if (
                    e.key === "Backspace" ||
                    e.key === "Delete" ||
                    e.key === "Tab" ||
                    e.key === "Escape" ||
                    e.key === "Enter" ||
                    e.key.startsWith("Arrow") ||
                    e.ctrlKey ||
                    e.metaKey
                  ) {
                    if (props.onKeyDown) props.onKeyDown(e);
                    return;
                  }
                  const allowed = isRangeMode ? /[0-9\/t o\-]/i : /[0-9\/]/;
                  if (!allowed.test(e.key)) {
                    e.preventDefault();
                  }
                }
                if (props.onKeyDown) props.onKeyDown(e);
              }}
              onInput={(e) => {
                if (!isReadOnly && options?.allowInput) {
                  const formatted = formatTypedDate(e.target.value, isRangeMode);
                  e.target.value = formatted;

                  if (!formatted) {
                    if (fp.current?.flatpickr && !fp.current.isSettingDate) {
                      const inst = fp.current.flatpickr;
                      try {
                        fp.current.isSettingDate = true;
                        inst.clear();
                      } finally {
                        fp.current.isSettingDate = false;
                      }
                    }
                  } else {
                    const parsed = flexibleParseDate(formatted);
                    if (parsed && fp.current?.flatpickr && !fp.current.isSettingDate) {
                      const inst = fp.current.flatpickr;
                      const datesArr = Array.isArray(parsed) ? parsed : [parsed];

                      const currentDates = inst.selectedDates || [];
                      const isSame = datesArr.length === currentDates.length &&
                        datesArr.every((date, idx) => {
                          const cur = currentDates[idx];
                          return date && cur && date.getTime() === cur.getTime();
                        });

                      if (!isSame) {
                        try {
                          fp.current.isSettingDate = true;
                          inst.setDate(datesArr, true);
                          if (datesArr[0] && inst.jumpToDate) {
                            inst.jumpToDate(datesArr[0]);
                          }
                        } finally {
                          fp.current.isSettingDate = false;
                        }
                      }
                    }
                  }
                }
                if (props.onInput) props.onInput(e);
              }}
              onBlur={(e) => {
                handleInvalidCleanup(
                  fp.current?.flatpickr?.selectedDates,
                  fp.current?.flatpickr
                );
                if (props.onBlur) props.onBlur(e);
              }}
              {...props}
              {...flatpickrProps}
            />
          );
        }}
      />
    );
  },
);

DatePicker.displayName = "DatePicker";

export { DatePicker };